/**
 * teams.ts
 * 
 * Microsoft Teams SDK 초기화 및 유틸리티 함수
 * 
 * 주요 기능:
 * - Teams SDK 초기화
 * - Teams 컨텍스트 감지
 * - Teams 환경에서 실행 중인지 확인
 * - Teams SSO 토큰 가져오기 (선택적)
 */

import * as microsoftTeams from '@microsoft/teams-js';

export interface TeamsContext {
  isInTeams: boolean;
  context?: microsoftTeams.app.Context;
  initialized: boolean;
}

let teamsContext: TeamsContext = {
  isInTeams: false,
  initialized: false,
};

/**
 * Teams SDK 초기화
 * Teams 환경에서 실행 중인 경우 SDK를 초기화합니다.
 */
export const initializeTeams = async (): Promise<TeamsContext> => {
  // 이미 초기화된 경우 반환
  if (teamsContext.initialized) {
    return teamsContext;
  }

  try {
    // Teams 환경인지 확인 (iframe 내부에서 실행 중인지)
    const isInIframe = window.self !== window.top || 
                       window.location !== window.parent.location ||
                       window.parent !== window.self;

    if (isInIframe) {
      // Teams SDK가 로드되었는지 확인
      if (typeof microsoftTeams !== 'undefined' && microsoftTeams.app) {
        // Teams 환경에서 실행 중 (iframe 내부)
        await microsoftTeams.app.initialize();
        
        const context = await microsoftTeams.app.getContext();
        
        teamsContext = {
          isInTeams: true,
          context,
          initialized: true,
        };
        
        console.log('Microsoft Teams SDK 초기화 완료', context);
        return teamsContext;
      }
    }
    
    // 일반 브라우저 환경 또는 SDK가 로드되지 않음
    teamsContext = {
      isInTeams: false,
      initialized: true,
    };
    return teamsContext;
  } catch (error) {
    // Teams SDK 초기화 실패 (일반 브라우저 환경일 가능성)
    console.log('Teams SDK 초기화 실패 (일반 브라우저 환경일 수 있음):', error);
    teamsContext = {
      isInTeams: false,
      initialized: true,
    };
    return teamsContext;
  }
};

/**
 * Teams 환경에서 실행 중인지 확인
 */
export const isInTeams = (): boolean => {
  return teamsContext.isInTeams;
};

/**
 * Teams 컨텍스트 가져오기
 */
export const getTeamsContext = (): TeamsContext => {
  return teamsContext;
};

/**
 * Teams 컨텍스트 업데이트
 */
export const updateTeamsContext = async (): Promise<void> => {
  if (teamsContext.isInTeams && teamsContext.initialized) {
    try {
      const context = await microsoftTeams.app.getContext();
      teamsContext.context = context;
    } catch (error) {
      console.error('Teams 컨텍스트 업데이트 실패:', error);
    }
  }
};

/**
 * Teams SSO 토큰 가져오기 (선택적)
 * Azure AD 앱 등록이 필요합니다.
 */
export const getTeamsSSOToken = async (): Promise<string | null> => {
  if (!teamsContext.isInTeams) {
    return null;
  }

  try {
    const token = await microsoftTeams.authentication.getAuthToken();
    return token;
  } catch (error) {
    console.error('Teams SSO 토큰 가져오기 실패:', error);
    return null;
  }
};

/**
 * Teams에서 앱 설정 페이지 열기
 */
export const openTeamsSettings = async (): Promise<void> => {
  if (!teamsContext.isInTeams) {
    return;
  }

  try {
    await microsoftTeams.pages.config.setValidityState(true);
    await microsoftTeams.pages.config.setConfig({
      suggestedDisplayName: 'MARU Student',
      contentUrl: window.location.origin,
    });
  } catch (error) {
    console.error('Teams 설정 페이지 열기 실패:', error);
  }
};

/**
 * Teams에서 페이지 리로드
 */
export const reloadTeamsPage = (): void => {
  if (teamsContext.isInTeams) {
    microsoftTeams.pages.backStack.navigateBack();
  } else {
    window.location.reload();
  }
};

/**
 * Teams에서 외부 링크 열기
 */
export const openTeamsLink = async (url: string): Promise<void> => {
  if (teamsContext.isInTeams) {
    try {
      await microsoftTeams.app.openLink(url);
    } catch (error) {
      console.error('Teams 링크 열기 실패:', error);
      window.open(url, '_blank');
    }
  } else {
    window.open(url, '_blank');
  }
};
